// Variants: a group product holds the options (Size: S, M, L; Color: Red…)
// and every combination is its own product (SKU, barcode, price, stock).
// Options and values are display-only JSON; nothing else reads them.
import type { JsonValue, ProductRow } from '@ims/data'

/** Option name → its values, in the order the user gave them. */
export type VariantOptions = Record<string, string[]>
/** Option name → this variant's value. */
export type VariantValues = Record<string, string>

export const MAX_OPTIONS = 3
export const MAX_VARIANTS = 100

/** "S, M , L,, M" → ["S", "M", "L"] (trimmed, repeats and blanks dropped). */
export function parseOptionValues(text: string): string[] {
  const out: string[] = []
  for (const part of text.split(',')) {
    const v = part.trim()
    if (v && !out.some((o) => o.toLowerCase() === v.toLowerCase())) out.push(v)
  }
  return out
}

/** The options stored on a group (anything malformed is ignored). */
export function readOptions(value: JsonValue | null | undefined): VariantOptions {
  const out: VariantOptions = {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) return out
  for (const [name, values] of Object.entries(value)) {
    if (Array.isArray(values)) out[name] = values.filter((v): v is string => typeof v === 'string')
  }
  return out
}

/** The values stored on a variant. */
export function readValues(value: JsonValue | null | undefined): VariantValues {
  const out: VariantValues = {}
  if (!value || typeof value !== 'object' || Array.isArray(value)) return out
  for (const [name, v] of Object.entries(value)) if (typeof v === 'string') out[name] = v
  return out
}

/** Every combination of the options, first option varying slowest. */
export function variantCombos(options: VariantOptions): VariantValues[] {
  const entries = Object.entries(options).filter(([, values]) => values.length > 0)
  if (entries.length === 0) return []
  let combos: VariantValues[] = [{}]
  for (const [name, values] of entries) {
    combos = combos.flatMap((c) => values.map((v) => ({ ...c, [name]: v })))
  }
  return combos
}

/** "M / Red", in the order of the group's options. */
export function variantLabel(values: VariantValues, options?: VariantOptions): string {
  const order = options ? Object.keys(options) : Object.keys(values)
  const seen = order.filter((k) => values[k] !== undefined)
  for (const k of Object.keys(values)) if (!seen.includes(k)) seen.push(k)
  return seen.map((k) => values[k]).join(' / ')
}

/** The variant's product name: "T-shirt · M / Red". */
export function variantName(groupName: string, values: VariantValues, options?: VariantOptions) {
  const label = variantLabel(values, options)
  return label ? `${groupName.trim()} · ${label}` : groupName.trim()
}

/** "TEE" + { Size: "M", Color: "Navy Blue" } → "TEE-M-NAVYBLUE". */
export function variantSku(baseSku: string, values: VariantValues, options?: VariantOptions) {
  const order = options ? Object.keys(options) : Object.keys(values)
  const parts = order
    .map((k) => values[k])
    .filter((v): v is string => !!v)
    .map((v) => v.toUpperCase().replace(/[^\p{L}\p{N}]+/gu, ''))
    .filter(Boolean)
  return [baseSku.trim(), ...parts].join('-').slice(0, 64)
}

/** A key that matches the same combination however it was typed. */
export function comboKey(values: VariantValues): string {
  return Object.entries(values)
    .map(([k, v]) => [k.trim().toLowerCase(), v.trim().toLowerCase()] as const)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${v}`)
    .join('|')
}

/**
 * Parses a variant written in a sheet: JSON ({"size": "M"}), "Size: M, Color: Red",
 * or plain values ("M / Red") matched to the given option names in order.
 */
export function parseVariantText(text: string, optionNames: readonly string[] = []): VariantValues {
  const s = text.trim()
  if (!s) return {}
  if (s.startsWith('{')) {
    try {
      const parsed = JSON.parse(s) as JsonValue
      const values: VariantValues = {}
      for (const [k, v] of Object.entries(readRecord(parsed))) {
        if (typeof v === 'string' || typeof v === 'number') values[titleCase(k)] = String(v).trim()
      }
      return values
    } catch {
      // Not JSON after all: read it as text below.
    }
  }
  const parts = s
    .split(/\s*[/|,;]\s*/)
    .map((p) => p.trim())
    .filter(Boolean)
  const values: VariantValues = {}
  parts.forEach((part, i) => {
    const m = /^([^:=]+)\s*[:=]\s*(.+)$/.exec(part)
    if (m) values[titleCase(m[1]!.trim())] = m[2]!.trim()
    else values[optionNames[i] ?? (parts.length === 1 ? 'Variant' : `Option ${i + 1}`)] = part
  })
  return values
}

function readRecord(value: JsonValue): Record<string, JsonValue> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {}
}

function titleCase(s: string): string {
  return s ? s[0]!.toUpperCase() + s.slice(1) : s
}

/** Adds a variant's values to a group's options (new values go last). */
export function mergeOptions(options: VariantOptions, values: VariantValues): VariantOptions {
  const out: VariantOptions = Object.fromEntries(
    Object.entries(options).map(([k, v]) => [k, [...v]]),
  )
  for (const [name, value] of Object.entries(values)) {
    const key = Object.keys(out).find((k) => k.toLowerCase() === name.toLowerCase()) ?? name
    const list = (out[key] ??= [])
    if (!list.some((v) => v.toLowerCase() === value.toLowerCase())) list.push(value)
  }
  return out
}

/** Fields a variant always shares with its group (copied on every group save). */
export const SHARED_FIELDS = [
  'description',
  'category_id',
  'unit_id',
  'tax_rate_id',
  'price_includes_tax',
  'track_stock',
  'is_active',
  'brand_id',
  'supplier_id',
  'manufacturer',
  'hs_code',
  'shelf_location',
  'tags',
  'attributes',
  'track_batches',
  'track_expiry',
  'shelf_life_days',
  'shelf_life_unit',
  'track_serials',
  'warranty_months',
  'min_stock_milli',
  'reorder_qty_milli',
  'kitchen_station',
  'for_sale',
  'diet',
  'spicy',
] as const satisfies readonly (keyof ProductRow)[]

/** Fields a variant follows from its group unless it has its own value. */
export const FOLLOWED_FIELDS = [
  'sell_price_paisa',
  'cost_price_paisa',
  'mrp_paisa',
  'wholesale_price_paisa',
  'min_price_paisa',
] as const

type Shared = Pick<ProductRow, (typeof SHARED_FIELDS)[number] | (typeof FOLLOWED_FIELDS)[number]>

/**
 * The changes a variant needs after its group changed from `before` to
 * `after`: shared fields always follow; prices follow when the variant still
 * had the group's old value. Null when nothing changes.
 */
export function variantChanges(
  variant: ProductRow,
  before: Shared,
  after: Shared & { name: string; variant_options: JsonValue | null },
): Partial<ProductRow> | null {
  const changes: Record<string, unknown> = {}
  for (const k of SHARED_FIELDS) {
    if (JSON.stringify(variant[k]) !== JSON.stringify(after[k])) changes[k] = after[k]
  }
  for (const k of FOLLOWED_FIELDS) {
    if (variant[k] === before[k] && variant[k] !== after[k]) changes[k] = after[k]
  }
  const name = variantName(
    after.name,
    readValues(variant.variant_values),
    readOptions(after.variant_options),
  )
  if (variant.name !== name) changes.name = name
  return Object.keys(changes).length > 0 ? (changes as Partial<ProductRow>) : null
}
